// Sentry must initialise before the app modules load (DE-1815).
import "./app/instrument";
import React from "react";
import "./index.css";
import "./app/i18n";
import {App} from "./app/App";

import {createRoot} from "react-dom/client";

import * as serviceWorker from './serviceWorker';
import * as Sentry from "@sentry/react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {getLocale} from "./app/dateFormat";
import ErrorIcon from "@mui/icons-material/Error";
import {Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {captureInstallPrompt} from "./install-app/installPromptStore";
import {installStaleChunkReload} from "./app/staleChunkReload";

// The browser offers installation once, possibly before the app renders (DE-1707).
captureInstallPrompt(window);

const sessionStorageOrUndefined = () => {
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
};

installStaleChunkReload({
    window,
    storage: sessionStorageOrUndefined(),
    now: Date.now,
    reload: () => window.location.reload(),
    fetch: (...args) => window.fetch(...args),
    document
});

const UnknownError = () => {
    const {code} = getLocale();

    const errorTranslations = {
        'nb': {
            'title': 'DAMS - Det har oppstått en ukjent feil',
            'button': 'Start på nytt',
        }, 'nn': {
            'title': 'DAMS - Det har oppstått ein ukjend feil',
            'button': 'Start på nytt',
        }, 'sv': {
            'title': 'DAMS - Ett okänt fel har uppstått',
            'button': 'Starta om',
        }, 'en': {
            'title': 'DAMS - An unknown error has occurred',
            'button': 'Restart',
        }
    };

    return (
        <Dialog open>
            <DialogTitle sx={{
                backgroundColor: '#ee0000',
                color: '#eee',
            }}>
                <Stack direction="row" spacing={2} sx={{
                    alignItems: "center"
                }}>
                    <ErrorIcon/>
                    <Typography sx={{marginLeft: '16px'}}>
                        <b>{errorTranslations[code].title}</b>
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Box sx={{marginTop: '32px'}}>
                    <img src={'/unknown_exception_duck.png'}
                         width={"300px"} height={"300px"}
                         alt={"Unknown Exception"}/>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => window.location.href = "/"} color="primary" variant="contained">
                    {errorTranslations[code].button}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const onSWUpdate = (registration) => {
    const event = new CustomEvent('serviceWorkerUpdate');
    document.dispatchEvent(event);
};

const devEnv = ['local', 'beta', 'dev', 'development'].includes(window._env_.REACT_APP_X_QUOTE_MODE);

const container = document.getElementById("root");
const root = createRoot(container, {
    // Error is thrown and not caught by ErrorBoundary
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Error caught in ErrorBoundary
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        if (devEnv) {
            console.error('caught error:', error, errorInfo.componentStack);
        }
    }),
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
        if (devEnv) {
            console.error('recoverable error:', error, errorInfo.componentStack);
        }
    })
});

root.render(<Sentry.ErrorBoundary fallback={<UnknownError/>}>
    <App/>
</Sentry.ErrorBoundary>);

// Register the Service Worker.
serviceWorker.register({
    onUpdate: onSWUpdate
});